import { RotateCcw } from 'lucide-react'
import { useEffect, useId, useImperativeHandle, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import type {
  TimelineMarks,
  TimelineMonth,
  TimelineStretch,
  TimelineValue,
} from '@/lib/audition/job-timeline'
import {
  planOnLine,
  STRETCH_LABELS,
  storeMarks,
  stretchCeilIndex,
  stretchFloorIndex,
  TIMELINE_STRETCHES,
  tapMonth,
  timelineGuidance,
  timelineTightness,
} from '@/lib/audition/job-timeline'
import { cn } from '@/lib/utils'

/** The numbered checklist, in the order the machine asks for the stretches. */
const CHECKLIST: Record<TimelineStretch, string> = {
  practice: 'Mark the months you see going mostly to interview prep.',
  interviewing: 'Mark the months you want to be interviewing seriously.',
  jobBy: 'Mark the window where you hope an offer lands.',
}

/**
 * How each stretch is drawn. Each is a step shorter than the one before and
 * all three rest on the same baseline, so marking one over another never
 * hides either. The offer window is thinnest and red because it is the hard
 * thing: everything else can slip, that cannot.
 */
const STRETCH_STYLE: Record<
  TimelineStretch,
  { bar: string; swatch: string; h: string }
> = {
  practice: { bar: 'bg-foreground/70', swatch: 'bg-foreground/70', h: 'h-7' },
  interviewing: {
    bar: 'bg-foreground/35',
    swatch: 'bg-foreground/35',
    h: 'h-[19px]',
  },
  jobBy: { bar: 'bg-destructive', swatch: 'bg-destructive', h: 'h-2.5' },
}

/** Transient interaction state. Only `marks` is ever stored; these are not. */
interface Painting {
  stretch: TimelineStretch
  pending: number | null
  refusal: string | null
}

function firstUnmarked(marks: TimelineMarks): TimelineStretch {
  return TIMELINE_STRETCHES.find((stretch) => !marks[stretch]) ?? 'practice'
}

/**
 * "When do you expect to be interviewing?" asked as three stretches painted
 * over one line of months, rather than picked from a list of windows that all
 * mean something different to everybody.
 *
 * Every rule and every sentence lives in the job-timeline module, which has no
 * DOM in it — the copy is the feature here, and a fence that lets through a
 * plan where the offer lands before the interviews is a content bug no render
 * test would catch. This file is the drawing and the taps.
 *
 * The marks are controlled by the parent; the half-finished tap, the stretch
 * in hand and the last refusal are local, because none of them is an answer.
 */
export function JobTimeline({
  label,
  value,
  onChange,
  disabled,
  months,
  describedBy,
  invalid,
}: {
  label: string
  value: TimelineValue
  onChange: (value: TimelineValue) => void
  disabled?: boolean
  /** Id of help text describing this group, hung on the group itself. */
  describedBy?: string
  /** Whether this group is why the step was refused. */
  invalid?: boolean
  /**
   * The line, built from the step's date rather than from a clock read here,
   * so it is the same line `ready` judges this answer against. See
   * `StepContext.today`.
   */
  months: TimelineMonth[]
}) {
  const { marks, notices, clearable } = planOnLine(value, months)
  const [painting, setPainting] = useState<Painting>(() => ({
    stretch: firstUnmarked(marks),
    pending: null,
    refusal: null,
  }))

  const { stretch, pending, refusal } = painting
  const floor = stretchFloorIndex(stretch, marks, months, pending)
  const ceil = stretchCeilIndex(stretch, months, pending)
  const guidance = timelineGuidance({ ...painting, marks }, months)
  const tight = timelineTightness(marks)
  const noticeId = useId()

  // Clearing unmounts the Clear button while it still has focus, which drops a
  // keyboard applicant onto <body> partway through the step. Focus goes to the
  // stretch the cleared line starts on — after the commit, not in `reset`,
  // because until then the checked radio is still the one from before.
  const pickerRef = useRef<{ focus: () => void }>(null)
  const [cleared, setCleared] = useState(false)
  useEffect(() => {
    if (!cleared) return
    pickerRef.current?.focus()
    setCleared(false)
  }, [cleared])

  const tap = (index: number) => {
    const next = tapMonth({ ...painting, marks }, months, index)
    setPainting({
      stretch: next.stretch,
      pending: next.pending,
      refusal: next.refusal,
    })
    if (next.marks !== marks) onChange(storeMarks(value, next.marks, months))
  }

  const reset = () => {
    setPainting({ stretch: 'practice', pending: null, refusal: null })
    setCleared(true)
    onChange({})
  }

  /** True while `k` is the stretch being painted and a first tap has landed. */
  const previewing = (k: TimelineStretch) => k === stretch && pending !== null

  /**
   * The band to draw for a stretch: the tap in hand where there is one, and only
   * otherwise what is marked. The preview has to win — re-drawing a stretch
   * that is already marked would otherwise show the old band the whole time
   * and give no sign that the next tap is about to replace it.
   */
  const bandFor = (k: TimelineStretch) =>
    previewing(k) && pending !== null
      ? { from: pending, to: pending }
      : (marks[k] ?? null)

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <ol className="space-y-0.5 text-sm">
            {TIMELINE_STRETCHES.map((k, i) => (
              <li key={k} className="flex gap-2">
                <span className="text-muted-foreground">{i + 1}.</span>
                <span
                  className={cn(
                    k === stretch && !marks[k] && 'font-medium',
                    marks[k] && 'text-muted-foreground line-through',
                  )}
                >
                  {CHECKLIST[k]}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-sm text-pretty text-muted-foreground">
            They can overlap, and they usually do. Real searches are messy. Most
            people are prepping, interviewing and waiting on offers all at once.
            Broad strokes are enough.
          </p>
        </div>
        {clearable && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={disabled}
          >
            <RotateCcw />
            Clear the timeline
          </Button>
        )}
      </div>

      <StretchPicker
        ref={pickerRef}
        value={stretch}
        disabled={!!disabled}
        onChange={(k) =>
          setPainting({ stretch: k, pending: null, refusal: null })
        }
      />

      {/* The columns never shrink below a thumb: twelve of them sharing a
          phone's content width would be ~28px each, and they are the primary
          input for this question — six taps to answer it. Under that width the
          line scrolls sideways instead. The bands scroll with it because they
          sit inside the same track rather than pinned to the outer box. */}
      {/* The negative margin buys room for a focus ring: `overflow-x` makes
          the other axis clip too, and a month column sits flush against the
          top edge, so its outset ring would be cut off on every tab stop. */}
      <div
        role="group"
        aria-label={label}
        aria-describedby={
          [describedBy, notices.length > 0 ? noticeId : '']
            .filter(Boolean)
            .join(' ') || undefined
        }
        aria-invalid={invalid || undefined}
        className="-m-1 overflow-x-auto p-1"
      >
        <div className="relative min-w-[38rem]">
          {/* The line and the bands over it, behind the month columns so the
            whole column — line, band and name — is one tap target. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-7"
          >
            <div className="absolute inset-0 rounded-md bg-muted" />
            {TIMELINE_STRETCHES.map((k) => {
              const band = bandFor(k)
              if (!band) return null
              return (
                <div
                  key={k}
                  className={cn(
                    'absolute bottom-0 rounded-md',
                    STRETCH_STYLE[k].bar,
                    STRETCH_STYLE[k].h,
                    previewing(k) && 'opacity-50',
                  )}
                  style={{
                    left: `${(band.from / months.length) * 100}%`,
                    right: `${100 - ((band.to + 1) / months.length) * 100}%`,
                  }}
                />
              )
            })}
          </div>

          <div className="flex">
            {months.map((month, index) => {
              const out = index < floor || index > ceil
              const stretches = TIMELINE_STRETCHES.filter((k) => {
                const band = bandFor(k)
                return !!band && index >= band.from && index <= band.to
              })
              return (
                <button
                  key={month.key}
                  type="button"
                  // Named so a screen reader hears the month and what is
                  // already marked on it, since the bands themselves are a
                  // drawing and say nothing.
                  aria-label={
                    stretches.length
                      ? `${month.name}: ${stretches.map((k) => STRETCH_LABELS[k].toLowerCase()).join(', ')}`
                      : month.name
                  }
                  // Out of reach, but still tappable on purpose: every fence
                  // has a sentence explaining itself and naming the month to
                  // reach for instead, and a `disabled` button can neither be
                  // clicked nor focused to hear it. aria-disabled says "not a
                  // valid choice right now" while leaving it operable.
                  aria-disabled={out || undefined}
                  disabled={disabled}
                  onClick={() => tap(index)}
                  className={cn(
                    'relative z-1 flex min-w-0 flex-1 flex-col items-center rounded-md pt-8 pb-1',
                    'outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                    'disabled:pointer-events-none disabled:opacity-40',
                    out ? 'opacity-40' : 'hover:bg-accent/50',
                  )}
                >
                  <span className="text-[11px] font-medium tracking-wide whitespace-nowrap uppercase">
                    {month.label}
                  </span>
                  {/* Never wraps: one column growing a second line would
                    put its neighbours' names on a different baseline. */}
                  <span className="text-[10px] whitespace-nowrap text-muted-foreground">
                    {month.sub || ' '}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Why a stretch they marked is not on the line any more. The group
          points at this too: without it a screen reader hears an unmarked
          stretch and nothing about the answer that used to be there. */}
      {notices.length > 0 && (
        <div id={noticeId} className="space-y-1">
          {notices.map((line) => (
            <p key={line} className="text-sm text-pretty">
              {line}
            </p>
          ))}
        </div>
      )}

      {/* What is marked so far and what to do next — always one or the other,
          because a two-tap gesture with no narration is a puzzle. */}
      <div aria-live="polite" className="space-y-1">
        {guidance.map((line) => (
          <p key={line} className="text-sm text-pretty text-muted-foreground">
            {line}
          </p>
        ))}
      </div>

      {refusal && (
        <p role="alert" className="text-sm text-pretty text-destructive">
          {refusal}
        </p>
      )}

      {tight && (
        <div className="space-y-1 rounded-lg border p-3">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <span
              aria-hidden="true"
              className="size-2.5 rounded-full bg-yellow-500"
            />
            {tight.heading}
          </p>
          <p className="text-xs text-pretty text-muted-foreground">
            {tight.body}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Which stretch the next tap draws — and the only way back to one that is
 * already marked, short of clearing the whole line.
 *
 * A radiogroup, not three toggle buttons. Exactly one stretch is ever in hand,
 * and `aria-pressed` on three buttons announces three independent switches
 * with nothing saying they are mutually exclusive. This follows ChipGroup's
 * single mode — roving tab stop, arrows move and select — rather than reusing
 * it, because each option carries the swatch that ties it to a band on the
 * line.
 */
function StretchPicker({
  ref,
  value,
  onChange,
  disabled,
}: {
  /** Focuses the checked option — the picker's one tab stop. */
  ref?: React.Ref<{ focus: () => void }>
  value: TimelineStretch
  onChange: (stretch: TimelineStretch) => void
  disabled: boolean
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  // The single tab stop. Falls back to the first option rather than leaving
  // every button at -1 if `value` is somehow off the list.
  const activeIndex = Math.max(TIMELINE_STRETCHES.indexOf(value), 0)
  useImperativeHandle(
    ref,
    () => ({ focus: () => refs.current[activeIndex]?.focus() }),
    [activeIndex],
  )

  const moveTo = (index: number) => {
    const i = (index + TIMELINE_STRETCHES.length) % TIMELINE_STRETCHES.length
    onChange(TIMELINE_STRETCHES[i])
    refs.current[i]?.focus()
  }

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const go = (to: number) => {
      event.preventDefault()
      moveTo(to)
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') go(index + 1)
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') go(index - 1)
    else if (event.key === 'Home') go(0)
    else if (event.key === 'End') go(TIMELINE_STRETCHES.length - 1)
  }

  return (
    /* Which band is which — and which one the next tap will draw. Also how you
       get back to a stretch you want to re-mark: without this the only way is
       to clear the whole line and start again. */
    <div
      role="radiogroup"
      aria-label="Which stretch the next tap marks"
      className="flex flex-wrap gap-x-2 gap-y-1"
    >
      {TIMELINE_STRETCHES.map((k, index) => (
        <button
          key={k}
          ref={(node) => {
            refs.current[index] = node
          }}
          type="button"
          role="radio"
          aria-checked={k === value}
          tabIndex={index === activeIndex ? 0 : -1}
          disabled={disabled}
          onClick={() => onChange(k)}
          onKeyDown={(event) => onKeyDown(event, index)}
          className={cn(
            // 44px tall: this is a real control, not a legend, and it is the
            // one that undoes a mis-tap on the line.
            'flex min-h-11 items-center gap-2 rounded-md px-2 text-xs',
            'outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'disabled:pointer-events-none disabled:opacity-40',
            // A checked radio has to look checked. Weight alone is the
            // faintest possible signal on the control that undoes a mis-tap,
            // and without a hover state nothing marks these as controls at
            // all — ChipGroup fills its checked option for the same reason.
            k === value
              ? 'bg-accent font-semibold ring-1 ring-border'
              : 'text-muted-foreground hover:bg-accent/50',
          )}
        >
          <span
            aria-hidden="true"
            className={cn('size-2 rounded-full', STRETCH_STYLE[k].swatch)}
          />
          {STRETCH_LABELS[k]}
        </button>
      ))}
    </div>
  )
}
